import { createContext, ReactNode, useContext } from 'react';
import { Tooltip } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { StepType } from '@/types/automationFields.ts';
import useRetrieveAutomationCapabilities from '@/hooks/api/steps/useRetrieveAutomationCapabilities.ts';

type CapabilityHelpContextValue = {
  helpForLabel: (label: ReactNode, capabilityFieldId?: string) => ReactNode;
};
const CapabilityHelpContext = createContext<CapabilityHelpContextValue | null>(null);

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

export const CapabilityHelpProvider = ({ stepType, children }: { stepType: StepType; children: ReactNode }) => {
  const { data } = useRetrieveAutomationCapabilities(stepType);
  const fields = data?.steps.find((step) => step.id === stepType)?.fields.filter((field) => field.visibility === 'public') ?? [];

  const helpForLabel = (label: ReactNode, capabilityFieldId?: string): ReactNode => {
    const field = capabilityFieldId
      ? fields.find((candidate) => candidate.id === capabilityFieldId)
      : typeof label === 'string'
        ? fields.find((candidate) => {
            const key = normalize(label);
            return normalize(candidate.label) === key || normalize(candidate.id) === key;
          })
        : undefined;
    if (!field) return label;
    return (
      <span>
        {label}{' '}
        <Tooltip label={`${field.summary}${field.non_effect ? ` ${field.non_effect}` : ''}`} multiline w={320} withArrow>
          <span tabIndex={0} aria-label={`Help for ${field.label}`}>
            <IconInfoCircle size={14} aria-hidden="true" />
          </span>
        </Tooltip>
      </span>
    );
  };

  return <CapabilityHelpContext.Provider value={{ helpForLabel }}>{children}</CapabilityHelpContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCapabilityHelpLabel = (label: ReactNode, capabilityFieldId?: string): ReactNode => {
  const context = useContext(CapabilityHelpContext);
  return context?.helpForLabel(label, capabilityFieldId) ?? label;
};
